// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import SignOut from "../popups/SignOut";
import { LuHome, LuPanelLeftClose, LuPanelLeftOpen, LuSearch, LuHistory, LuMoreHorizontal, LuChevronDown, LuHelpCircle, LuUserCircle2, LuLogOut, LuCloudOff } from "react-icons/lu";
import { GenieAIIcon } from "../icons";
import RailTip from "../tooltip/RailTip";
import OrgsDropDown from "../dropdowns/OrgsDropDown";
import DashboardListBar from "./DashboardListBar";
import RecentsListBar from "./RecentsListBar";
import HelpAndSupportPanel from "./HelpAndSupportPanel";
import HelpGuide from "../common/HelpGuide";
import { useHelpGuide } from "../../hooks/useHelpGuide";
import NewDashboardBox from "../tooltip/NewDashboardBox";
import RecentDashboardBox from "../tooltip/RecentDashboardBox";
import SearchBar from "./SearchBar";
import { useSelector, useDispatch } from "react-redux";
import {
  logoutFailure,
  setActiveTourComponent,
  setActiveDashboard,
  iframePop,
  startIsLoading,
  stopIsLoading,
  setSidebarWidth,
  requestSidebarClose
} from "../../store/auth/action";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeftForkTourCard from "../tours/LeftForkTourCard"; // OLD tour card - kept for reference, no longer rendered below
import TourCard from "../tours/TourCard";
import TourAnchor from "../tours/TourAnchor";
import UnsubscribedPop from "../popups/UnsubscribedPop";
import SalesPopup from "../popups/IframePop";
import { logoutUser } from "../../services/auth.service";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";
import { getSession } from "../../utils/sessionstore";
import { handleItem } from "../../utils/handleItem";
import { withAssetVersion } from "../../utils/functions";
import { useSidebarDashboards, isGenieAI } from "../../hooks/useToggleFavourite";
import { prefetchUserProfile } from "../../utils/profileCache";
import { isHiddenCategoryLabel } from "../../utils/productPageConfig";

const Divider = ({ inset = false }) => (
  <div className={`${inset ? "mx-3" : ""} self-stretch h-px shrink-0 bg-general-border`} />
);

const SECTION_PREVIEW_COUNT = 6;

const visibleCount = (total) =>
  total > SECTION_PREVIEW_COUNT ? SECTION_PREVIEW_COUNT - 1 : total;
const RECENTS_PREVIEW_COUNT = 5;

const SideBar = () => {
  const { clientID } = useParams();
  const { type } = useParams();

  const { isSalesPopUp, userDetails, orgs, isIframePop, activedashboard, lobbyStartTime, dashboardStartTime,subscriptions, logo, clientname, isTourActive, isTourStarter, activeTourComponent, showLaunchCampaign, sidebarCloseRequest } = useSelector((state) => ({
    isSalesPopUp: state.isSalesPopUp,
    userDetails: state.userDetails,
    orgs: state.orgs,
    isIframePop: state.isIframePop,
    activedashboard: state.activedashboard,
    lobbyStartTime: state.lobbyStartTime,
    dashboardStartTime: state.dashboardStartTime,
    subscriptions: state.subscriptions,
    logo: state.logo,
    clientname: state.clientname,
    isTourActive: state.isTourActive,
    isTourStarter: state.isTourStarter,
    activeTourComponent: state.activeTourComponent,
    showLaunchCampaign: state.showLaunchCampaign,
    sidebarCloseRequest: state.sidebarclose_request,
  }));
  // NEW - hover-opened flyouts (search/recents/category dropdowns) should stay
  // disabled during the "first page" welcome popup too (isTourStarter), not
  // just the step-by-step tour (isTourActive) - see the guards below.
  const isTourShowing = isTourActive || isTourStarter;

  const sidebarDashboards = useSidebarDashboards();

  const [openSignOut, setOpenSignOut] = useState(false);
  const [openHelpSupport, setOpenHelpSupport] = useState(false);
  const helpGuide = useHelpGuide({ triggerType: "HELP_AND_SUPPORT_PANEL" });
  const [salesContent, setSalesContent] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [openedCat, setOpenCat] = useState("");
  const [openedGroup, setOpenedGroup] = useState("");
  const [expandBar, setExpandBar] = useState(false);
  const [openDashboardList, setOpenDashboardList] = useState(false);
  const [overflowCategory, setOverflowCategory] = useState(null);
  const [openRecentsList, setOpenRecentsList] = useState(false);
  const [openSearchFlyout, setOpenSearchFlyout] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentsOverflowOnly, setRecentsOverflowOnly] = useState(false);
  const [listTop, setListTop] = useState(0);
  const [listLeft, setListLeft] = useState(60);
  const [barWidth, setBarWidth] = useState(288);
  const sessionId = getSession("session_id");

  useEffect(() => {
    dispatch(setSidebarWidth(expandBar ? barWidth : 60));
  }, [expandBar, barWidth]);

  const token = getSession("session_hwai");
  const client_id = getSession("client_id");
  const location = useLocation();

  // Auto-close the Help & Support panel (open or minimized) whenever the user
  // navigates to a different report/page, so it doesn't linger over content it
  // no longer relates to.
  useEffect(() => {
    setOpenHelpSupport(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!openSearchFlyout) {
      setSearchTerm("");
      return;
    }

    const onMouseDown = (event) => {
      if (searchFlyoutRef.current?.contains(event.target)) return;
      if (event.target.closest?.(".sidebar")) return;
      setOpenSearchFlyout(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenSearchFlyout(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openSearchFlyout]);

  const isAdmin =
    orgs?.length > 0 && userDetails?.permissions === "HWAI_ADMIN";

  // Routes whose content shifts with the rail, so it stays as the user left it.
  // The landing page is the root route, so it is whatever has no sub-route.
  const isLanding =
    /^\/[^/]*\/?$/.test(location.pathname) &&
    !location.pathname.includes("login");
  const shiftsWithSidebar =
    isLanding ||
    location.pathname.includes("/product/") ||
    location.pathname.includes("/profile") ||
    location.pathname.includes("/earnings-intelligence") ||
    location.pathname.includes("/dashboard/");
  const shiftsWithSidebarRef = useRef(shiftsWithSidebar);
  shiftsWithSidebarRef.current = shiftsWithSidebar;

  const expandSidebarRef = useRef(null);
  const railRef = useRef(null);
  const listCloseTimer = useRef(null);
  const overflowPinned = useRef(false);
  const searchFlyoutRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const openedProductRef = useRef(null);
  // First/last of the first 5 category rows - used to measure a combined
  // highlight box spanning all 5 together for the "Modules" tour step.
  const firstModuleRowRef = useRef(null);
  const lastModuleRowRef = useRef(null);
  const [modulesTourBox, setModulesTourBox] = useState(null);

  const isModulesTourStep = isTourActive && activeTourComponent?.name === "Modules";
  useLayoutEffect(() => {
    if (!isModulesTourStep) {
      setModulesTourBox(null);
      return;
    }
    const update = () => {
      const first = firstModuleRowRef.current?.getBoundingClientRect();
      const last = lastModuleRowRef.current?.getBoundingClientRect();
      if (!first || !last) return;
      setModulesTourBox({ top: first.top, left: 0, width: 60, height: last.bottom - first.top });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isModulesTourStep]);

  useEffect(() => {
    if (!expandBar || !openedCat) return;
    const scroller = scrollAreaRef.current;
    const product = openedProductRef.current;
    if (!scroller || !product) return;

    const id = requestAnimationFrame(() => {
      const productRect = product.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const overflow = productRect.bottom - scrollerRect.bottom;
      if (overflow <= 0) return;
      const headroom = productRect.top - scrollerRect.top;
      scroller.scrollBy({ top: Math.min(overflow, headroom), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [openedCat, openedGroup, expandBar]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Lets content outside the sidebar (the AEP timeline) collapse it after
  // opening a dashboard, the way the sidebar's own links do.
  useEffect(() => {
    if (!sidebarCloseRequest) return;
    afterLandDashboard();
    dispatch(requestSidebarClose(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarCloseRequest]);
  function cancelSignout() {
    setOpenSignOut(false);
  }
  async function logOut() {
    try {
      // dispatch(logoutRequest());
      dispatch(startIsLoading());
      setOpenSignOut(false);
      // dispatch(logoutSuccess());
      await logoutUser({activedashboard, type, token, session_id: sessionId, userDetails, clientname, client_id, lobbyStartTime, dashboardStartTime, endTime: new Date()});
      navigate("/login");

    } catch (err) {
      dispatch(logoutFailure(err.message));
    }
  }

  function handleClickOutside(event) {
    if (
      expandSidebarRef.current &&
      !expandSidebarRef.current.contains(event.target) &&
      !event.target.classList.contains("sidebar")
    ) {
      setOpenDashboardList(false);
      overflowPinned.current = false;
      setOverflowCategory(null);
      setOpenRecentsList(false);
      setOpenSearchFlyout(false);
      if (!shiftsWithSidebarRef.current) setExpandBar(false);
    }
  }

  function goHome() {
    const address = location.pathname.split("/").filter((part, ind) => ind !== 0 && part.trim() !== "");
    dispatch(setActiveDashboard({}));
    setOpenDashboardList(false);
    if (userDetails?.permissions === "HWAI_ADMIN" && clientID !== undefined) {
      if (address.length !== 1) {
        logAmplitudeEvent(amplitude, "Home", {
          eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
        });
        navigate(`/${clientID}`);
      }
    } else {
      if (address.length !== 0) {
        logAmplitudeEvent(amplitude, "Home", {
          eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
        });
        navigate("/");
      }
    }
  }


  function startResize(e) {
    e.preventDefault();
    const onMove = (ev) =>
      setBarWidth(Math.min(420, Math.max(288, ev.clientX)));
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  // Give the pointer a moment to cross the gap into the flyout before closing
  function scheduleListClose() {
    clearTimeout(listCloseTimer.current);
    listCloseTimer.current = setTimeout(() => {
      setOpenDashboardList(false);
      if (!overflowPinned.current) setOverflowCategory(null);
      setOpenRecentsList(false);
      // Keep the search panel while its input is in use; it closes on click
      // away or Escape instead.
      if (!searchFlyoutRef.current?.contains(document.activeElement)) {
        setOpenSearchFlyout(false);
      }
    }, 200);
  }

  function cancelListClose() {
    clearTimeout(listCloseTimer.current);
  }

  function toggleProductCategories(subscription) {
    setOpenCat(openedCat === subscription.label ? "" : subscription.label);
    setOpenedGroup("");
    setSelectedSubscription(subscription);
  }

  // Product page navigation is parked for now; re-enable the commented
  // openProductPage calls on the product row to turn it back on.
  // eslint-disable-next-line no-unused-vars
  function openProductPage(subscription) {
    setOpenDashboardList(false);
    overflowPinned.current = false;
    setOverflowCategory(null);
    setOpenRecentsList(false);
    dispatch(setActiveDashboard({}));
    const base =
      clientID !== undefined &&
      orgs.map((org) => org._id).includes(clientID) &&
      userDetails?.permissions === "HWAI_ADMIN"
        ? `/${clientID}`
        : "";
    navigate(`${base}/product/${subscription._id}`);
  }

  // NEW - hover-opened flyout panels (category overflow, recents, search,
  // product dropdown) shouldn't pop open over the tour's dimming/spotlight
  // overlays while a tour step is active - they'd visually clash with
  // whatever the current step is trying to highlight. Guarded at the shared
  // open-functions/handlers themselves rather than hiding the buttons, so
  // hover still just does nothing (no visual dead zone) during the tour.
  function openCategoryOverflow(e, subscription, category) {
    if (isTourShowing) return;
    cancelListClose();
    setOpenRecentsList(false);
    setOpenDashboardList(false);
    setOverflowCategory({
      subscription,
      category: {
        ...category,
        dashboardIds: category.dashboardIds.slice(
          visibleCount(category.dashboardIds.length)
        ),
      },
    });
    setListTop(e.currentTarget.getBoundingClientRect().top);
    setListLeft(railRef.current?.getBoundingClientRect().right || 60);
  }

  function openRecentsFlyout(e, overflowOnly = false) {
    if (isTourShowing) return;
    setOpenSearchFlyout(false);
    setRecentsOverflowOnly(overflowOnly);
    cancelListClose();
    setOpenDashboardList(false);
    overflowPinned.current = false;
    setOverflowCategory(null);
    setListTop(e.currentTarget.getBoundingClientRect().top);
    setListLeft(railRef.current?.getBoundingClientRect().right || 60);
    setOpenRecentsList(true);
  }

  // Genie AI has its own dedicated row, so it is kept out of the product tree.
  const visibleSubscriptions = useMemo(
    () =>
      (subscriptions || []).map((subscription) => ({
        ...subscription,
        productcategoryIds: (subscription.productcategoryIds || []).map((category) => ({
          ...category,
          dashboardIds: (category.dashboardIds || []).filter(
            (dashboard) => !isGenieAI(dashboard)
          ),
        })),
      })),
    [subscriptions]
  );

  const activeProductLabel = useMemo(() => {
    if (!activedashboard?._id) return "";
    for (const subscription of visibleSubscriptions) {
      for (const category of subscription.productcategoryIds || []) {
        if ((category.dashboardIds || []).some((dash) => dash?._id === activedashboard._id))
          return subscription.label;
      }
    }
    return "";
  }, [visibleSubscriptions, activedashboard]);

  const searchResults = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return [];

    const seen = new Set();
    const matches = [];
    for (const subscription of visibleSubscriptions) {
      for (const category of subscription.productcategoryIds || []) {
        for (const dashboard of category.dashboardIds || []) {
          if (!dashboard?.label?.toLowerCase().includes(needle)) continue;
          if (seen.has(dashboard._id)) continue;
          seen.add(dashboard._id);
          matches.push({ dashboard, productLine: subscription.label });
        }
      }
    }
    return matches;
  }, [visibleSubscriptions, searchTerm]);

  async function landSearchResult(dashboard, productLine) {
    await handleItem({
      productLine: productLine,
      clientID: clientID ?? getSession("client_id"),
      dashboard: dashboard,
      dispatch: dispatch,
      navigate: navigate,
      userDetails: userDetails,
      clientName: clientname,
      pathname: location.pathname,
      triggerType: "SEARCH_DASHBOARD",
    });
    setSearchTerm("");
    setOpenSearchFlyout(false);
    afterLandDashboard();
  }

  function afterLandDashboard() {
    setOpenDashboardList(false);
    overflowPinned.current = false;
    setOverflowCategory(null);
    setOpenRecentsList(false);
    setOpenSearchFlyout(false);
    setExpandBar(false);
  }

  async function openChatbot() {
    const standardName = "GenieAI";
    if (!subscriptions?.length || !standardName) return;

    let localProductLine = "";
    let matchedDashboard = null;

    for (const subscription of subscriptions) {
      const categories = subscription.productcategoryIds || [];

      for (const category of categories) {
        const dashboards = category.dashboardIds || [];

        // Find dashboard by standardName instead of ID
        const match = dashboards.find(
          dash => dash.standarddashboardId?.standardName === standardName
        );

        if (match) {
          matchedDashboard = match;
          localProductLine = subscription.label;
          break;
        }
      }

      if (localProductLine) break; // exit outer loop
    }
    if (!matchedDashboard) {
      console.log(`No dashboard found for standardName: ${standardName}`);
      return;
    }
    await handleItem({ productLine: localProductLine, clientID: clientID ?? getSession("client_id"), userDetails: userDetails, dashboard: matchedDashboard, clientName: clientname, triggerType: "SIDEBAR_DASHBOARD", pathname: location.pathname, postItemHandle: afterLandDashboard, dispatch: dispatch, navigate: navigate});
  }


  // OLD: Nav step used to go straight to "Favourite" (step 2 of 6). Replaced
  // below - the new tour inserts a "Search" step in between (step 2 of 10),
  // so Nav now hands off to it instead.
  // function onTourNext() {
  //   dispatch(setActiveTourComponent({ name: "Favourite", number: 2 }));
  // }
  function onTourNext() {
    dispatch(setActiveTourComponent({ name: "Search", number: 2 }));
  }

  // NEW: "Search" step's own Next handler - hands off to the new "Recent" step.
  function onSearchTourNext() {
    dispatch(setActiveTourComponent({ name: "Recent", number: 3 }));
  }

  // NEW: "Recent" step's own Next handler - hands off to the new "GenieAI" step.
  function onRecentTourNext() {
    dispatch(setActiveTourComponent({ name: "GenieAI", number: 4 }));
  }

  // NEW: "GenieAI" step's own Next handler - hands off to the new "Modules" step.
  function onGenieAITourNext() {
    dispatch(setActiveTourComponent({ name: "Modules", number: 5 }));
  }

  // NEW: "Modules" step's own Next handler - hands off to the new "Resources" step.
  function onModulesTourNext() {
    dispatch(setActiveTourComponent({ name: "Resources", number: 6 }));
  }

  // NEW: "Resources" step's own Next handler - hands off to the new "Help" step.
  function onResourcesTourNext() {
    dispatch(setActiveTourComponent({ name: "Help", number: 7 }));
  }

  // OLD (this session): "Help" step used to hand off to "Favourite" (step 8).
  // Superseded below - the tour's last 3 steps now follow the dashboard content
  // (AEP timeline -> What's New -> Explore More) instead of Favourite/Trending/
  // Ticket, since the Header.jsx-hosted "Ticket" step was never reachable (Header
  // is not rendered anywhere in the app) and the new Figma steps target real,
  // already-built dashboard blocks instead.
  // function onHelpTourNext() {
  //   dispatch(setActiveTourComponent({ name: "Favourite", number: 8 }));
  // }
  function onHelpTourNext() {
    dispatch(setActiveTourComponent({ name: "AepTimeline", number: 8 }));
  }

  // NEW - auto-skip for steps whose target isn't always there, same reasoning
  // as LandingPageGrid.jsx's AEP/What's New skip: without this, landing on a
  // step whose host condition is false renders nothing, and since nothing ever
  // fires that step's own "Next" handler, the tour would freeze there forever
  // (the exact dead-end bug found in the OLD tour's Favourite -> Banner hop).
  // - "Recent" needs sidebarDashboards.length > 0 (its host `{...&&...}` block).
  // - "GenieAI" needs showLaunchCampaign (its host `{...&&...}` block).
  // - "Modules" needs at least 5 subscriptions - it measures a box spanning
  //   rows 0 through 4 (see firstModuleRowRef/lastModuleRowRef below), which
  //   never completes with fewer than 5.
  // - "Resources" needs some subscription actually labeled "Resources" - see
  //   isResourcesTarget further down.
  const hasResourcesCategory = subscriptions?.some(
    (subscription) => subscription.label?.trim().toLowerCase() === "resources"
  );
  useEffect(() => {
    if (!isTourActive) return;
    const name = activeTourComponent?.name;
    if (name === "Recent" && !(sidebarDashboards.length > 0)) {
      dispatch(setActiveTourComponent({ name: "GenieAI", number: 4 }));
    } else if (name === "GenieAI" && !showLaunchCampaign) {
      dispatch(setActiveTourComponent({ name: "Modules", number: 5 }));
    } else if (name === "Modules" && (subscriptions?.length || 0) < 5) {
      dispatch(setActiveTourComponent({ name: "Resources", number: 6 }));
    } else if (name === "Resources" && !hasResourcesCategory) {
      dispatch(setActiveTourComponent({ name: "Help", number: 7 }));
    }
  }, [isTourActive, activeTourComponent, sidebarDashboards, showLaunchCampaign, subscriptions, hasResourcesCategory, dispatch]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(listCloseTimer.current);
    };
  }, []);

  // The collapsed rail hover writes openedCat, which the expanded tree reads.
  useEffect(() => {
    setOpenDashboardList(false);
    overflowPinned.current = false;
    setOverflowCategory(null);
    setOpenSearchFlyout(false);
    if (expandBar) {
      setOpenCat("");
      setOpenedGroup("");
    }
  }, [expandBar]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.salesContent) {
        setSalesContent(event.data.salesContent);


        setTimeout(() => {
          dispatch(iframePop());
        }, 0);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [dispatch]);

  return (
    <>
      <div
        onMouseLeave={() => {
          setOpenDashboardList(false);
          scheduleListClose();
        }}
        ref={expandSidebarRef}
        className="font-geist flex sidebar z-50 h-screen fixed bottom-0 left-0 top-0 rounded-none justify-between"
      >
        <div
          ref={railRef}
          style={expandBar ? { width: barWidth } : undefined}
          className={`${expandBar ? "relative" : "w-[60px] shrink-0"} bg-general-background border-r border-general-border justify-between flex flex-col h-full overflow-hidden`}
        >
          {expandBar && (
            <div
              onMouseDown={startResize}
              className="absolute bottom-0 right-0 top-0 z-10 w-1 cursor-col-resize hover:bg-general-primary/20 ani"
            />
          )}
          {!expandBar && (
            <div className="shrink-0 flex items-center justify-center pt-2">
              <RailTip title="Home">
                <img
                  alt="HWAI"
                  src={`https://hwlobbystorer1.blob.core.windows.net/sectionicons/hwaiicon.png?v=1`}
                  className="size-5 object-contain cursor-pointer select-none"
                  onClick={() => {
                    logAmplitudeEvent(amplitude, "Logo", {
                      eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                    });
                    goHome();
                  }}
                />
              </RailTip>
            </div>
          )}

          {/* reponsible to expand sidebar */}
          {!expandBar && (
            <div className="shrink-0 flex items-center justify-center py-2">
              {/* Portaled to document.body (see TourAnchor) instead of a plain absolute
                  child - the collapsed rail below has overflow-hidden, which was
                  clipping the card/arrow when it was positioned as a normal descendant. */}
              <TourAnchor
                show={isTourActive && activeTourComponent?.name === "Nav"}
                card={
                  <TourCard
                    onNext={onTourNext}
                    title={"Collapsible Navigation Bar"}
                    description={"Expand or Collapse the side navigation panel as needed."}
                    number={1}
                  />
                }
              >
                <RailTip title="Expand sidebar" show={!(isTourActive && activeTourComponent?.name === "Nav")}>
                  <button
                    type="button"
                    className="h-8 w-9 rounded-md text-general-muted-foreground hover:bg-general-accent ani flex items-center justify-center"
                    onClick={() => {
                      logAmplitudeEvent(amplitude, "Expand Sidebar", {
                        eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                      });
                      setExpandBar(true);
                    }}
                  >
                    <LuPanelLeftOpen className="size-4" />
                  </button>
                </RailTip>
              </TourAnchor>
            </div>
          )}

          {/* responsible to contract sidebar */}
          {expandBar && (
            <div className="shrink-0 flex items-center justify-between px-3 py-2.5">
              <img
                alt="HWAI"
                src="https://hwlobbystorer1.blob.core.windows.net/images/HWAI_Logo_Full.svg?v=1"
                className="h-6 w-auto object-contain cursor-pointer select-none"
                onClick={() => {
                  logAmplitudeEvent(amplitude, "Logo", {
                    eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                  });
                  goHome();
                }}
              />
              <RailTip title="Collapse sidebar">
                <button
                  type="button"
                  className="h-8 w-8 rounded-md text-general-muted-foreground hover:bg-general-accent ani flex items-center justify-center"
                  onClick={() => {
                    logAmplitudeEvent(amplitude, "Collapse Sidebar", {
                      eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                    });
                    setExpandBar(false);
                    setOpenDashboardList(false);
                  }}
                >
                  <LuPanelLeftClose className="size-4" />
                </button>
              </RailTip>
            </div>
          )}

          {!expandBar && <Divider inset />}

          {/* Section 1 including product list */}

          <div ref={scrollAreaRef} className={`${expandBar ? "hover-scrollbar min-h-0 flex-1 overflow-y-auto items-start" : "min-h-0 flex-1 overflow-y-auto scrollbar-hide items-center"} self-stretch flex flex-col justify-start`}>

            {expandBar ? (
              <div className="self-stretch shrink-0 px-3 pb-2 flex flex-col justify-start items-start gap-2">
                {isAdmin && <OrgsDropDown />}
                <SearchBar afterLandDashboard={afterLandDashboard} />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 py-2">
                {/* Portaled to document.body (see TourAnchor) instead of a plain absolute
                    child - the collapsed rail below has overflow-hidden, which was
                    clipping the card/arrow when it was positioned as a normal descendant. */}
                <TourAnchor
                  show={isTourActive && activeTourComponent?.name === "Search"}
                  card={
                    <TourCard
                      onNext={onSearchTourNext}
                      title={"Search"}
                      description={"Search any dashboard by name and access it in one click."}
                      number={2}
                    />
                  }
                  highlightIcon={<LuSearch className="size-4 text-general-muted-foreground" />}
                  highlightWidth={60}
                >
                  <RailTip title="Search" show={!openSearchFlyout && !(isTourActive && activeTourComponent?.name === "Search")}>
                    <button
                      type="button"
                      className="h-8 w-9 rounded-md hover:bg-general-accent ani flex items-center justify-center"
                      onMouseEnter={(e) => {
                        if (isTourShowing) return;
                        cancelListClose();
                        setOpenDashboardList(false);
                        setOverflowCategory(null);
                        setOpenRecentsList(false);
                        setListTop(e.currentTarget.getBoundingClientRect().top);
                        setListLeft(railRef.current?.getBoundingClientRect().right || 60);
                        setOpenSearchFlyout(true);
                      }}
                      onMouseLeave={() => scheduleListClose()}
                    >
                      <LuSearch className="size-4 text-general-muted-foreground" />
                    </button>
                  </RailTip>
                </TourAnchor>

                {sidebarDashboards.length > 0 && (
                  <>
                    {/* NEW tour step 3 ("Recent") - same portal pattern as Nav/Search. */}
                    <TourAnchor
                      show={isTourActive && activeTourComponent?.name === "Recent"}
                      card={
                        <TourCard
                          onNext={onRecentTourNext}
                          title={"Recent"}
                          description={"Quickly access recently viewed dashboards and pin up to 3 favorites for easy access."}
                          number={3}
                        />
                      }
                      highlightIcon={<LuHistory className="size-4 text-general-muted-foreground" />}
                      highlightWidth={60}
                    >
                      <RailTip title="Recents" show={!openRecentsList && !(isTourActive && activeTourComponent?.name === "Recent")}>
                        <button
                          type="button"
                          className="h-8 w-9 rounded-md hover:bg-general-accent ani flex items-center justify-center"
                          onClick={() => {
                            setExpandBar(true);
                            setOpenRecentsList(false);
                          }}
                          onMouseEnter={openRecentsFlyout}
                          onMouseLeave={() => scheduleListClose()}
                        >
                          <LuHistory className="size-4 text-general-muted-foreground" />
                        </button>
                      </RailTip>
                    </TourAnchor>
                    <div className="self-stretch h-px shrink-0 bg-general-border" />
                  </>
                )}

                {/* Hover highlight suppressed while a tour step is showing, so this
                    icon doesn't visually compete with the tour's own target icon. */}
                <RailTip title="Home" show={!isTourActive}>
                  <button
                    type="button"
                    className={`h-8 w-9 rounded-md ani flex items-center justify-center ${
                      isTourActive && activeTourComponent?.name === "Nav"
                        ? "bg-general-accent"
                        : isTourActive
                        ? ""
                        : isLanding
                        ? "bg-general-secondary"
                        : "hover:bg-general-accent"
                    }`}
                    onClick={goHome}
                    onMouseEnter={() => setOpenRecentsList(false)}
                  >
                    <LuHome className="size-4 text-general-muted-foreground" />
                  </button>
                </RailTip>

                {showLaunchCampaign && (
                  <>
                    {/* NEW tour step 4 ("GenieAI") - same portal pattern as Nav/Search/Recent. */}
                    <TourAnchor
                      show={isTourActive && activeTourComponent?.name === "GenieAI"}
                      card={
                        <TourCard
                          onNext={onGenieAITourNext}
                          title={"GenieAI"}
                          description={"Ask questions naturally and get data and insights powered by GenieAI across Market and Benefit data."}
                          number={4}
                        />
                      }
                      highlightIcon={<GenieAIIcon />}
                      highlightWidth={60}
                    >
                      <RailTip title="GenieAI" show={!(isTourActive && activeTourComponent?.name === "GenieAI")}>
                        <button
                          type="button"
                          className={`h-8 w-9 rounded-md ${isGenieAI(activedashboard) ? "bg-general-secondary" : "hover:bg-general-accent"} ani flex items-center justify-center`}
                          onClick={() => openChatbot()}
                          onMouseEnter={() => setOpenRecentsList(false)}
                        >
                          <GenieAIIcon />
                        </button>
                      </RailTip>
                    </TourAnchor>
                  </>
                )}

              </div>
            )}
            {/* Recents starts */}
            {expandBar && sidebarDashboards.length > 0 && (
              <>
                <div className="self-stretch py-1 flex flex-col justify-start items-start">
                  <p className="self-stretch px-3 pb-1 pt-1 text-left text-general-muted-foreground text-xs font-medium leading-4">
                    {"Recents"}
                  </p>
                  {sidebarDashboards.slice(0, RECENTS_PREVIEW_COUNT).map((dashboard) => (
                    <RecentDashboardBox
                      key={dashboard?._id}
                      dashboard={dashboard}
                      afterlandDashboardNew={afterLandDashboard}
                    />
                  ))}
                  {sidebarDashboards.length > RECENTS_PREVIEW_COUNT && (
                    <button
                      type="button"
                      onMouseEnter={(e) => openRecentsFlyout(e, true)}
                      onMouseLeave={() => scheduleListClose()}
                      className="h-8 shrink-0 w-[calc(100%-4px)] mx-auto px-3 rounded-md text-general-muted-foreground hover:bg-general-accent ani flex cursor-pointer justify-start items-center gap-2"
                    >
                      <LuMoreHorizontal className="size-4 shrink-0" />
                      <span className="text-sm font-normal leading-5">{"More"}</span>
                    </button>
                  )}
                </div>
                <Divider inset />
              </>
            )}
            {/* Recents ends */}

            {expandBar && (
              <div
                className={`h-8 shrink-0 w-[calc(100%-4px)] mx-auto mt-2 px-3 rounded-md ${isLanding ? "bg-general-secondary" : "hover:bg-general-accent"} ani flex cursor-pointer justify-start items-center gap-2`}
                onClick={goHome}
              >
                <LuHome className="size-4 shrink-0 text-general-muted-foreground" />
                <div className="flex-1 text-left text-general-foreground text-sm font-normal leading-5">
                  {"Home"}
                </div>
              </div>
            )}

            {/* chatbot section*/}
            {expandBar && showLaunchCampaign && (
              <div
                className={`h-8 shrink-0 w-[calc(100%-4px)] mx-auto px-3 rounded-md ${isGenieAI(activedashboard) ? "bg-general-secondary" : "hover:bg-general-accent"} ani flex cursor-pointer justify-start items-center gap-2`}
                onClick={() => openChatbot()}
              >
                <GenieAIIcon />
                <p className="flex-1 text-left text-general-foreground text-sm font-normal leading-5 cursor-pointer">
                  {"GenieAI"}
                </p>
              </div>
            )}

            {!expandBar && <Divider inset />}

            {/* List Of products starts  */}

            <div
              className={`${expandBar ? "items-start" : "items-center py-2 gap-1"} self-stretch flex flex-col justify-start`}
              onMouseLeave={() => scheduleListClose()}
            >
              {visibleSubscriptions.map((subscription, subIndex) => {
                // NEW tour step 6 ("Resources") targets the category actually
                // labeled "Resources" - matched by name rather than a hardcoded
                // index, since its position in the list isn't fixed across clients
                // (PdfViewerPopup.jsx already treats "Resources" as a real,
                // known category label the same way). Step 5 ("Modules") targets
                // the first 5 together as one group - measured separately below via
                // firstModuleRowRef/lastModuleRowRef instead of per-icon, since it
                // spans several rows.
                const isResourcesTarget = subscription.label?.trim().toLowerCase() === "resources" && !expandBar;
                // The product stands in for the open dashboard only while that
                // dashboard's own row is not on screen.
                const isProductActive =
                  activeProductLabel === subscription.label &&
                  (!expandBar || openedCat !== subscription.label);
                const productButton = (
                  <div
                    className={`group/product ${expandBar ? "h-8 shrink-0 w-[calc(100%-4px)] mx-auto px-3 justify-start gap-2" : "h-8 w-9 shrink-0 justify-center"} ${isProductActive ? "bg-general-secondary" : "hover:bg-general-accent"} rounded-md flex cursor-pointer items-center`}
                    onClick={() => {
                      if (expandBar) {
                        toggleProductCategories(subscription);
                      } else {
                        // openProductPage(subscription);
                        setExpandBar(true);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (expandBar || isTourShowing) return;
                      cancelListClose();
                      setOpenRecentsList(false);
                      setOpenSearchFlyout(false);
                      setSelectedSubscription(subscription);
                      setOpenCat(subscription.label);
                      setListTop(e.currentTarget.getBoundingClientRect().top);
                      setListLeft(
                        railRef.current?.getBoundingClientRect().right || 60
                      );
                      setOpenDashboardList(true);
                    }}
                  >
                    <span className="size-4 shrink-0 relative flex items-center justify-center">
                      <img
                        alt="Product-Icon"
                        src={withAssetVersion(subscription.icon)}
                        className={`cursor-pointer p-0 m-0 product-icons shrink-0 ${expandBar ? `${openedCat === subscription.label ? "opacity-0" : ""} group-hover/product:opacity-0 ani` : "opacity-100"}`}
                      />
                      {expandBar && (
                        <LuChevronDown
                          className={`${openedCat === subscription.label ? "rotate-180 opacity-100" : "opacity-0"} size-3.5 text-general-muted-foreground absolute group-hover/product:opacity-100 ani`}
                        />
                      )}
                    </span>
                    <div
                      className={`${!expandBar ? "hidden" : "flex justify-start items-center w-full gap-2"}`}
                    >
                      <p
                        className={`flex-1 min-w-0 truncate text-left text-general-foreground text-sm leading-5 cursor-pointer ${openedCat === subscription.label ? "font-semibold" : "font-normal"}`}
                        // onClick={(e) => {
                        //   e.stopPropagation();
                        //   openProductPage(subscription);
                        // }}
                      >
                        {subscription.label}
                      </p>
                    </div>
                  </div>
                );
                return (
                <div
                  ref={(el) => {
                    if (openedCat === subscription.label) openedProductRef.current = el;
                    if (subIndex === 0) firstModuleRowRef.current = el;
                    if (subIndex === 4) lastModuleRowRef.current = el;
                  }}
                  className={`${expandBar ? "items-start" : "items-center gap-1"} self-stretch flex flex-col justify-start`}
                  key={subscription._id}
                >
                {isResourcesTarget ? (
                  <TourAnchor
                    show={isTourActive && activeTourComponent?.name === "Resources"}
                    card={
                      <TourCard
                        onNext={onResourcesTourNext}
                        title={"Resources"}
                        description={"Access release notes, platform training recordings, and HealthWorksAI articles in one place."}
                        number={6}
                      />
                    }
                    highlightIcon={<img alt="" src={withAssetVersion(subscription.icon)} className="size-4" />}
                    highlightWidth={60}
                  >
                    {productButton}
                  </TourAnchor>
                ) : (
                  productButton
                )}

                {expandBar && openedCat === subscription.label && (
                  <div className="self-stretch flex flex-col justify-start items-start">
                    {subscription.productcategoryIds?.map((category) => {
                      const isOthers = category.label === `${subscription.label}_Others`;
                      const hasLabel =
                        Boolean(category.label?.trim()) && !isHiddenCategoryLabel(category);
                      const grouped =
                        subscription.enableGrouping === "true" && !isOthers && hasLabel;
                      const isOpen = openedGroup === category._id;

                      return (
                        <div className="self-stretch flex flex-col justify-start items-start" key={category._id}>
                          {grouped ? (
                            <div
                              className={`${isOpen ? "bg-general-accent" : ""} h-8 w-[calc(100%-4px)] mx-auto pl-9 pr-3 rounded-md hover:bg-general-accent ani flex cursor-pointer justify-start items-center gap-2`}
                              onClick={() => setOpenedGroup(isOpen ? "" : category._id)}
                            >
                              <span className="shrink-0 flex items-center justify-center">
                                <LuChevronDown
                                  className={`${isOpen ? "rotate-180" : ""} size-3.5 text-general-muted-foreground ani`}
                                />
                              </span>
                              <RailTip title={category.label} whenTruncated>
                                <span className="flex-1 min-w-0 truncate text-left text-general-foreground text-sm font-normal leading-5">
                                  {category.label}
                                </span>
                              </RailTip>
                            </div>
                          ) : (
                            !isOthers && hasLabel && (
                              <div className="flex h-8 w-full items-center pl-11 pr-2">
                                <RailTip title={category.label} whenTruncated>
                                  <div className="min-w-0 flex-1 truncate text-left text-general-muted-foreground text-xs font-medium">
                                    {category.label}
                                  </div>
                                </RailTip>
                              </div>
                            )
                          )}
                          {(!grouped || isOpen) &&
                            category.dashboardIds
                              ?.slice(0, visibleCount(category.dashboardIds.length))
                              .map((dashboard) => (
                                <NewDashboardBox
                                  key={dashboard?._id}
                                  afterlandDashboardNew={afterLandDashboard}
                                  dashboard={dashboard}
                                  view={true}
                                  indentClass={grouped ? "pl-[52px] pr-3" : "pl-11 pr-3" }
                                  categoryLabel={category.label}
                                />
                              ))}
                          {(!grouped || isOpen) &&
                            category.dashboardIds?.length > SECTION_PREVIEW_COUNT && (
                              <button
                                type="button"
                                onMouseEnter={(e) => openCategoryOverflow(e, subscription, category)}
                                onMouseLeave={() => scheduleListClose()}
                                className={`h-8 shrink-0 w-[calc(100%-4px)] mx-auto ${grouped ? "pl-[52px] pr-3" : "pl-11 pr-3"} rounded-md text-general-muted-foreground hover:bg-general-accent ani flex cursor-pointer justify-start items-center gap-2`}
                              >
                                <LuMoreHorizontal className="size-4 shrink-0" />
                                <span className="text-sm font-normal leading-5">{"More"}</span>
                              </button>
                            )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
                );
              })}
            </div>

            {/* NEW tour step 5 ("Modules") - highlights the first 5 category icons
                together as one group (measured via firstModuleRowRef/lastModuleRowRef
                above). Unlike the single-icon TourAnchor steps, this doesn't clone
                the icons - it leaves a gap in the dimming overlay covering exactly
                this rect (Home.jsx skips its own dimming for the "Modules" step),
                so the real icons show through completely untouched - same position,
                same size - bright against the sidebar's own white background, with
                dark pieces above/below to still dim everything else. */}
            {isModulesTourStep &&
              modulesTourBox &&
              createPortal(
                <>
                  {/* "Spotlight" technique instead of separate top/bottom dark strips -
                      rounding THOSE strips' own corners (tried first) revealed an ugly
                      white notch right at the seam next to the icons instead of a clean
                      curve, because the corner-radius was cut into the dark strip's own
                      edge rather than into the hole. The inner div is transparent and
                      sized exactly to the 5-icon rect; its box-shadow spreads a dark fill
                      to make a hole with clean rounded corners, no artifacts, without
                      touching the real icons at all. That box-shadow spread is 9999px in
                      EVERY direction though, so left unclipped it also darkens the main
                      content area on top of Home.jsx's own dim there - stacking to look
                      too dark ("landing page jyada dark ho jata hai"). The outer wrapper
                      clips it to just the 60px sidebar column via overflow-hidden, same
                      as the old top/bottom-strip version did, leaving the main content
                      dimmed exactly once, like every other step. */}
                  <div
                    className="fixed left-0 top-0 h-screen z-[55] overflow-hidden pointer-events-none"
                    style={{ width: 60 }}
                  >
                    <div
                      className="absolute rounded-lg"
                      style={{
                        top: modulesTourBox.top,
                        left: 0,
                        width: 60,
                        height: modulesTourBox.height,
                        boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)",
                      }}
                    />
                  </div>
                  <div
                    className="fixed z-[60]"
                    style={{ top: modulesTourBox.top, left: modulesTourBox.left + modulesTourBox.width + 22 }}
                  >
                    <TourCard
                      onNext={onModulesTourNext}
                      title={"Modules"}
                      description={"Access dashboards across Market, Product, Network, and Explainability modules. Click a module to expand and view its dashboards, hover over More to access all reports."}
                      number={5}
                    />
                  </div>
                </>,
                document.body
              )}

            {/* List Of Products Ends */}

          </div>
          {/* Section 1 ends */}

          {/* Section 2 including tour, profile and sign out icons */}
          <div className={`${expandBar ? "items-start pr-2.5" : "items-center"} self-stretch shrink-0 bg-general-background flex flex-col justify-start`}>
            <Divider inset />

            <div className={`${expandBar ? "shrink-0 items-center gap-1 px-3 py-2" : "flex-col items-center py-2 gap-1"} flex`}>
            {/* NEW tour step 7 ("Help") - same portal pattern as Nav/Search/etc. */}
            <TourAnchor
              show={isTourActive && activeTourComponent?.name === "Help"}
              card={
                <TourCard
                  onNext={onHelpTourNext}
                  title={"Help"}
                  description={"Access dashboard user guides or raise and track support tickets."}
                  number={7}
                />
              }
              highlightIcon={<LuHelpCircle className="size-4 shrink-0 text-general-muted-foreground" />}
              highlightWidth={60}
            >
              <RailTip title="Help" show={!(isTourActive && activeTourComponent?.name === "Help")}>
                <button
                  type="button"
                  onClick={() => {
                    logAmplitudeEvent(amplitude, "Help", {
                      eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                    });
                    setExpandBar(false);
                    setOpenDashboardList(false);
                    setOpenHelpSupport(true);
                  }}
                  className="h-8 w-9 shrink-0 justify-center rounded-md hover:bg-general-accent ani flex cursor-pointer items-center"
                >
                  <LuHelpCircle className="size-4 shrink-0 text-general-muted-foreground" />
                </button>
              </RailTip>
            </TourAnchor>
            <RailTip title={userDetails?.firstName || "Profile"}>
              <div
                className="h-8 w-9 shrink-0 justify-center rounded-md hover:bg-general-accent ani flex cursor-pointer items-center"
                onMouseEnter={() => {
                  import("../../layouts/ProfileLayout");
                  prefetchUserProfile();
                }}
                onClick={() => {
                  logAmplitudeEvent(amplitude, "Profile", {
                    eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                  });
                  setOpenDashboardList(false);
                  dispatch(setActiveDashboard({}));
                  if (
                    clientID !== undefined &&
                    orgs.map((org) => org._id).includes(clientID) &&
                    userDetails?.permissions === "HWAI_ADMIN"
                  ) {
                    navigate(`/${clientID}/profile`);
                  } else {
                    navigate("/profile");
                  }
                }}
              >
                <LuUserCircle2 className="size-4 shrink-0 text-general-muted-foreground" />
              </div>
            </RailTip>
            <RailTip title="Logout">
              <div
                className="h-8 w-9 shrink-0 justify-center rounded-md hover:bg-general-accent ani flex cursor-pointer items-center"
                onClick={() => {setOpenSignOut(true);}}
              >
                <LuLogOut className="size-4 shrink-0 text-general-muted-foreground" />
              </div>
            </RailTip>
            </div>
          </div>
        </div>
        {/* Section 2 ends */}

        {overflowCategory && (
          <DashboardListBar
            dashBoardCategories={[overflowCategory.category]}
            subscription={overflowCategory.subscription}
            afterLandDashboard={afterLandDashboard}
            view={false}
            top={listTop}
            left={listLeft}
            showCategoryLabels={false}
            onEnter={cancelListClose}
            onClose={() => { overflowPinned.current = false; setOverflowCategory(null); }}
            onPinChange={(pinned) => { overflowPinned.current = pinned; }}
          />
        )}

        {openDashboardList && !expandBar && (
          <DashboardListBar
            dashBoardCategories={selectedSubscription.productcategoryIds}
            subscription={selectedSubscription}
            afterLandDashboard={afterLandDashboard}
            view={selectedSubscription.enableGrouping === "true" ? true : false}
            top={listTop}
            left={listLeft}
            onEnter={cancelListClose}
            onClose={() => setOpenDashboardList(false)}
          />
        )}

        {openSearchFlyout && !expandBar && (
          <div
            ref={searchFlyoutRef}
            className="dashboard-flyout fixed flex items-start"
            style={{ top: listTop, left: listLeft + 2 }}
            onMouseEnter={cancelListClose}
            onMouseLeave={() => scheduleListClose()}
          >
            <div className="font-geist w-72 bg-card-card rounded-lg shadow-lg outline outline-1 outline-general-border overflow-hidden">
              <div className="flex items-center gap-2 border-b border-general-border px-3 py-2">
                <LuSearch className="size-4 shrink-0 text-general-muted-foreground" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => {
                    logAmplitudeEvent(amplitude, "Search", {
                      eventTimestamp:new Date().toLocaleString('en-US', { timeZone: 'UTC' })
                    });
                  }}
                  placeholder="Search"
                  className="flex-1 min-w-0 bg-transparent text-general-foreground text-sm font-normal leading-5 outline-none placeholder:text-general-muted-foreground"
                />
              </div>
              {searchTerm.trim().length > 0 && (
                <div className="app-scrollbar max-h-64 py-1 flex flex-col justify-start items-start overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map(({ dashboard, productLine }) => (
                      <p
                        key={dashboard._id}
                        className="self-stretch min-h-8 px-3 py-1.5 hover:bg-general-accent ani cursor-pointer truncate text-left text-general-foreground text-sm font-normal leading-5"
                        onClick={() => landSearchResult(dashboard, productLine)}
                      >
                        {dashboard.label}
                      </p>
                    ))
                  ) : (
                    <div className="self-stretch py-4 flex flex-col justify-start items-center gap-4">
                      <div className="size-10 p-2 bg-general-secondary rounded-lg flex justify-center items-center">
                        <LuCloudOff className="size-6 text-general-primary" />
                      </div>
                      <div className="flex flex-col justify-start items-center gap-1">
                        <div className="self-stretch text-center text-general-primary text-base font-medium leading-6">
                          {"No data to display"}
                        </div>
                        <div className="text-center text-general-muted-foreground text-sm font-normal leading-5">
                          {"Try changing the name and search again."}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="min-h-9 px-4 py-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border hover:bg-[rgba(0,0,0,0.05)] ani flex justify-center items-center gap-2"
                      >
                        <span className="text-center text-general-foreground text-sm font-medium leading-5">
                          {"Clear Search"}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {openRecentsList && sidebarDashboards.length > 0 && (
          <RecentsListBar
            dashboards={
              recentsOverflowOnly
                ? sidebarDashboards.slice(RECENTS_PREVIEW_COUNT)
                : sidebarDashboards
            }
            afterLandDashboard={afterLandDashboard}
            top={listTop}
            left={listLeft}
            onEnter={cancelListClose}
            onClose={() => setOpenRecentsList(false)}
            showTitle={!recentsOverflowOnly}
          />
        )}

        {/* OLD tour step 1 card ("Nav") - replaced by the new Figma design below.
        {isTourActive && activeTourComponent?.name === "Nav" && (
          <div className=" ml-4 mt-3">
            <LeftForkTourCard
              onNext={onTourNext}
              icon="https://hwlobbystorer1.blob.core.windows.net/tour/navtour.svg"
              title={"Collapsible Navigation Bar"}
              description={
                "Our dashboard lists have been reorganized for easier navigation. All products are now conveniently located in a two-fold collapsible sidebar."
              }
              number={1}
            />
          </div>
        )}
        */}
        {/* SUPERSEDED - this render used the same disconnected-margin approach as the
        old card (just `ml-4 mt-3` from here, unrelated to the actual button's DOM
        position), which is why the arrow was pointing at the wrong icon. Moved next
        to the real "Expand sidebar" button above instead, so it's always accurate.
        {isTourActive && activeTourComponent?.name === "Nav" && (
          <div className="ml-4 mt-3">
            <TourCard
              onNext={onTourNext}
              title={"Collapsible Navigation Bar"}
              description={"Expand or Collapse the side navigation panel as needed."}
              number={1}
            />
          </div>
        )}
        */}
      </div>

      {/* Sign out pop up open on click of signOut icon */}
      {isSalesPopUp && <UnsubscribedPop />}
      {isIframePop && <SalesPopup content={salesContent} />}
      {openSignOut && <SignOut onCancel={cancelSignout} onConfirm={logOut} />}
      {openHelpSupport && (
        <HelpAndSupportPanel
          onClose={() => setOpenHelpSupport(false)}
          onOpenHelpGuide={helpGuide.openHelpGuide}
          onHelpGuideHover={helpGuide.prefetch}
        />
      )}
      <HelpGuide
        sectionLog={helpGuide.sectionLog}
        chapterLog={helpGuide.chapterLog}
        open={helpGuide.open}
        setOpen={helpGuide.setOpen}
        data={helpGuide.helpObj}
        setData={helpGuide.setHelpObj}
      />
    </>
  );
};

export default memo(SideBar);
